<template>
  <o-dialog
    ref="dialog"
    :title="title"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.save'), type: 'confirm', variant: 'success', callback: confirm },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div v-if="discountModel" class="">
      <o-form ref="form">
        <o-field-group v-slot="{ status }" :label="$t('general.title')" class="w-full mb-6 block" rules="required">
          <text-field v-model="discountModel.title" :status="status" />
        </o-field-group>

        <o-field-group
          v-slot="{ status }"
          :label="$t('general.price')"
          :name="$t('general.price')"
          class="mb-6 block"
          :rules="isAmountTypePercentage ? 'required|between:0,100' : 'required|currency'"
        >
          <div class="flex items-center space-x-2">
            <currency-field v-if="isAmountTypeFixed" v-model="_price" :status="status" class="w-1/2" />
            <number-field
              v-if="isAmountTypePercentage"
              v-model="_price"
              :min="0"
              :max="100"
              slim
              :status="status"
              trailing="%"
              class="w-1/2"
            />
            <segment-field v-model="discountModel.amountType" :options="discountAmountTypeOptions" class="border w-1/2" />
          </div>
        </o-field-group>

        <o-field-group :label="$t('general.tax')" class="block w-full mb-6">
          <segment-field v-model="discountModel.discountTaxType" :options="discountTaxTypeOptions" class="border" />
        </o-field-group>

        <o-field-group :label="$t('general.locked')" :info="$t('components.discountModel.actions.edit.lockedInfo')" class="w-full mb-6">
          <toggle-field v-model="discountModel.locked" />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { OFieldGroup } from '@/components/elements'
import { TextField, CurrencyField, NumberField, ToggleField, SegmentField } from '@/components/fields'

import { DiscountAmountType, DiscountAmountTypeLabels, DiscountTaxType, DiscountTaxTypeLabels } from '~/_api/models/discountModel'
import type { DiscountModel } from '~/_api/models/discountModel'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success', discountModel: DiscountModel): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const discountModel = ref<DiscountModel | null>(null)

const title = computed(() => {
  if (discountModel.value && isNewRecord.value) {
    return $i18n.t('components.discountModel.actions.edit.new')
  }

  return $i18n.t('components.discountModel.actions.edit.edit')
})

const _price = computed({
  get () {
    return discountModel.value?.price
  },
  set (val) {
    if (discountModel.value) {
      discountModel.value.price = Number(val)
    }
  },
})

const isNewRecord = computed(() => {
  return discountModel.value?.isNewRecord ?? true
})

const isAmountTypeFixed = computed(() => {
  return discountModel.value?.isAmountTypeFixed ?? false
})

const isAmountTypePercentage = computed(() => {
  return discountModel.value?.isAmountTypePercentage ?? false
})

const discountAmountTypeOptions = computed(() => {
  return Object.values(DiscountAmountType)
    .filter((v) => typeof v === 'number')
    .map((item) => {
      return {
        label: $i18n.t(DiscountAmountTypeLabels[item as DiscountAmountType]),
        value: item,
      }
    })
})

const discountTaxTypeOptions = computed(() => {
  return Object.values(DiscountTaxType)
    .filter((v) => typeof v === 'number')
    .map((item) => {
      return {
        label: $i18n.t(DiscountTaxTypeLabels[item as DiscountTaxType]),
        value: item,
      }
    })
})

const open = async (model?: DiscountModel) => {
  try {
    if (model) {
      discountModel.value = model
    } else {
      const response = await $api.discountModel.defaults(null as any)
      discountModel.value = response.data
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }

  dialog.value.open()
}

const confirm = async () => {
  if (!discountModel.value) return

  emit('update:loading', true)

  try {
    const response = await $api.discountModel.save(discountModel.value)
    emit('update:loading', false)

    if (response.success) {
      $notification.toast({
        title: $i18n.t('components.discountModel.actions.edit.success'),
        text: $i18n.t('components.discountModel.actions.edit.successInfo', { title: discountModel.value.title }),
        variant: 'success',
      })

      emit('success', discountModel.value)

      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })

    emit('error')
  }
}

const validate = () => {
  return form.value.validate()
}

defineExpose({ open })
</script>
